
import {
  BaseDevice,
  DeviceType,
  DeviceTypeId,
  SystemType,
  SystemTypeId,
} from 'types/devices.types';

/**
 * Checks if a device matches a given device type (by string or id).
 * @param {BaseDevice | null | undefined} device - The device object to check.
 * @param {DeviceType | DeviceTypeId} type - The device type (string or id) to compare against.
 * @returns {boolean} True if the device matches the type, false otherwise.
 */
export function DeviceIsDeviceType(
  device: BaseDevice | null | undefined,
  type: DeviceType | DeviceTypeId,
): boolean {
  if (!device) return false;
  if (typeof type === 'number') return device.device_type_id === type;
  if (typeof type === 'string') {
    // Normalize both strings: trim, lowercase, and remove all whitespace
    const normalize = (str: string) => str.replace(/\s+/g, '').toLowerCase();
    if (device.device_type && normalize(device.device_type) === normalize(type)) return true;
  }
  return false;
}

/**
 * Checks if a device matches a given system type (by string or id).
 * @param {BaseDevice | null | undefined} device - The device object to check.
 * @param {SystemType | SystemTypeId} systemType - The system type (string or id) to compare against.
 * @returns {boolean} True if the device matches the system type, false otherwise.
 */
export function DeviceIsSystemType(
  device: BaseDevice | null | undefined,
  systemType: SystemType | SystemTypeId,
): boolean {
  if (!device) return false;
  if (typeof systemType === 'number') return device.system_type_id === systemType;
  if (typeof systemType === 'string') {
    // Normalize both strings: trim, lowercase, and remove all whitespace
    const normalize = (str: string) => str.replace(/\s+/g, '').toLowerCase();
    if (device.system_type && normalize(device.system_type) === normalize(systemType)) return true;
  }
  return false;
}

/**
 * Resolves the system type of a device as a SystemType or SystemTypeId.
 * Returns the string system_type if available, otherwise the numeric system_type_id.
 */

/**
 * Resolves the system type of a device as a string (SystemType).
 * @param {BaseDevice | null | undefined} device - The device object to resolve.
 * @returns {SystemType | undefined} The string system_type if available, otherwise undefined.
 */
export function ResolveDeviceSystem(device: BaseDevice | null | undefined): SystemType | undefined {
  if (!device) return undefined;
  if (device.system_type) return device.system_type as SystemType;
  return undefined;
}

/**
 * Resolves the system type id of a device as a number (SystemTypeId).
 * @param {BaseDevice | null | undefined} device - The device object to resolve.
 * @returns {SystemTypeId | undefined} The numeric system_type_id if available, otherwise undefined.
 */
export function ResolveDeviceSystemId(device: BaseDevice | null | undefined): SystemTypeId | undefined {
  if (!device) return undefined;
  if (typeof device.system_type_id !== 'undefined') return device.system_type_id as SystemTypeId;
  return undefined;
}

/**
 * Resolves the device type as a DeviceType or DeviceTypeId.
 * Returns the string device_type if available, otherwise the numeric device_type_id.
 */

/**
 * Resolves the device type as a string (DeviceType).
 * @param {BaseDevice | null | undefined} device - The device object to resolve.
 * @returns {DeviceType | undefined} The string device_type if available, otherwise undefined.
 */
export function ResolveDeviceType(device: BaseDevice | null | undefined): DeviceType | undefined {
  if (!device) return undefined;
  if (device.device_type) return device.device_type as DeviceType;
  return undefined;
}

/**
 * Resolves the device type id as a number (DeviceTypeId).
 * @param {BaseDevice | null | undefined} device - The device object to resolve.
 * @returns {DeviceTypeId | undefined} The numeric device_type_id if available, otherwise undefined.
 */
export function ResolveDeviceTypeId(device: BaseDevice | null | undefined): DeviceTypeId | undefined {
  if (!device) return undefined;
  if (typeof device.device_type_id !== 'undefined') return device.device_type_id as DeviceTypeId;
  return undefined;
}
